import React from "react";
import { format } from "date-fns";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import type { FilterNode } from "./LogsFilterBuilder";
import type { ApiFilter, LogsHistogramBucket } from "./request";

export type LogLevel = "ERROR" | "WARN" | "INFO" | "DEBUG" | "TRACE";

export const LOG_LEVELS: LogLevel[] = ["ERROR", "WARN", "INFO", "DEBUG", "TRACE"];

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  reflectorId: string;
  reflectorName: string;
  reflectorUuid: string;
  source: string;
  message: string;
  metadata?: Record<string, unknown>;
}

type TagVariant = "success" | "warning" | "error" | "default" | "neutral";

export const LEVEL_TAG_VARIANT: Record<LogLevel, TagVariant> = {
  ERROR: "error",
  WARN: "warning",
  INFO: "success",
  DEBUG: "neutral",
  TRACE: "default",
};

export const LEVEL_BAR_COLOR: Record<LogLevel, string> = {
  ERROR: "var(--semantic-bg-danger, #ef4444)",
  WARN: "var(--semantic-bg-warning, #f59e0b)",
  INFO: "var(--semantic-bg-success, #10b981)",
  DEBUG: "var(--semantic-bg-brand-secondary, #6366f1)",
  TRACE: "var(--semantic-bg-tertiary, #9ca3af)",
};

export const LOG_FILTER_KEYS = [
  "Reflector ID",
  "Name",
  "UUID",
  "Level",
  "Source",
  "Message",
];

/**
 * Maps the user-facing filter keys from the chip bar to the API field names
 * expected by `POST /v2/reflector/logs/search`. Keys are matched
 * case-insensitive and tolerate extra spaces.
 */
const FILTER_KEY_TO_API: Record<string, string> = {
  "reflector id": "reflectorId",
  reflectorid: "reflectorId",
  name: "reflectorName",
  uuid: "reflectorUuid",
  level: "level",
  source: "source",
  service: "service",
  component: "component",
  message: "message",
  direction: "direction",
  msgid: "msgid",
  iterationid: "iterationId",
};

/** Translate a FilterNode[] (from LogsFilterBuilder) to the API filter shape. */
export function nodesToApiFilters(nodes: FilterNode[]): ApiFilter[] {
  return nodes.map((node, i) => {
    const { filter } = node;
    const rawKey = (filter.key || "").trim().toLowerCase();
    const apiField = rawKey ? FILTER_KEY_TO_API[rawKey] : undefined;
    const op: ":" | "=" = filter.op === "=" ? "=" : ":";
    return {
      field: apiField,
      op,
      value: String(filter.value ?? ""),
      joinOp: i === 0 ? undefined : node.op || "AND",
      ...(node.not ? { not: true } : {}),
    };
  });
}

/** Normalize a backend log level string to our typed LogLevel. */
export function normalizeLevel(raw: unknown): LogLevel {
  const up = String(raw ?? "").toUpperCase();
  if (
    up === "ERROR" ||
    up === "WARN" ||
    up === "INFO" ||
    up === "DEBUG" ||
    up === "TRACE"
  ) {
    return up;
  }
  // Common aliases seen in structured log pipelines.
  if (up === "WARNING") return "WARN";
  if (up === "FATAL" || up === "CRITICAL") return "ERROR";
  return "INFO";
}

export type { LogsHistogramBucket } from "./request";

/** Count docs per level inside a histogram bucket array (stats strip). */
export function levelCountsFromHistogram(
  buckets: LogsHistogramBucket[] | null
): Record<LogLevel, number> {
  const out: Record<LogLevel, number> = {
    ERROR: 0,
    WARN: 0,
    INFO: 0,
    DEBUG: 0,
    TRACE: 0,
  };
  if (!buckets) return out;
  for (const b of buckets) {
    for (const lvl of LOG_LEVELS) {
      out[lvl] += Number(b[lvl] || 0);
    }
  }
  return out;
}

export const logColumns: GenericTableColumn<LogEntry>[] = [
  {
    header: "Time",
    accessor: "timestamp",
    width: "170px",
    sortable: true,
    renderCell: (row) => (
      <span className="font-mono text-xs text-semantic-text-secondary whitespace-nowrap">
        {format(new Date(row.timestamp), "yyyy-MM-dd HH:mm:ss")}
      </span>
    ),
  },
  {
    header: "Level",
    accessor: "level",
    width: "90px",
    sortable: true,
    renderCell: (row) => (
      <GenericTag
        label={row.level}
        variant={LEVEL_TAG_VARIANT[row.level]}
        size="small"
        rounded="full"
        minWidth="60px"
      />
    ),
  },
  {
    header: "Reflector",
    accessor: "reflectorId",
    width: "220px",
    sortable: true,
    renderCell: (row) => (
      <div
        className="flex flex-col leading-tight"
        title={`Name: ${row.reflectorName}\nUUID: ${row.reflectorUuid}`}
      >
        <span className="font-mono text-xs text-semantic-text-primary truncate">
          {row.reflectorId}
        </span>
        <span className="font-mono text-[10px] text-semantic-text-secondary truncate">
          {row.reflectorName}
          {row.reflectorUuid ? ` · ${row.reflectorUuid.slice(0, 8)}…` : ""}
        </span>
      </div>
    ),
  },
  {
    header: "Source",
    accessor: "source",
    width: "140px",
    sortable: true,
    renderCell: (row) =>
      row.source ? (
        <GenericTag
          label={row.source}
          variant="neutral"
          size="small"
          rounded="small"
        />
      ) : null,
  },
  {
    header: "Message",
    accessor: "message",
    sortable: false,
    wrapMode: "truncate",
    renderCell: (row) => (
      <span
        className="font-mono text-xs text-semantic-text-primary"
        title={row.message}
      >
        {row.message}
      </span>
    ),
  },
];
